import {
  Box,
  Typography,
  Divider,
  useTheme,
  Grid,
  Button,
  Stack,
  IconButton,
  FormHelperText,
} from "@mui/material";
import CustomDropzone from "global/components/CustomDropzone/CustomDropzone";
import DropZoneIcon from "assets/icons1/dropzone_icon.svg";
import CustomDialog from "global/components/CustomDialog/CustomDialog";
import { useState } from "react";
import {
  handleErrorMessage,
  isTruthy,
  openSuccessNotification,
} from "helpers/methods";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { exportGroup, updateContactListDocument } from "../GroupsService";
import CustomLoader from "global/components/CustomLoader/CustomLoader";

interface CustomProps {
  isVisible: boolean;
  handleDialogClose: Function;
  group?: any;
  getActiveData: any;
}

const UpdateGroupModal = (props: CustomProps) => {
  const theme = useTheme();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [groupToBeUpdated, setGroupToBeUpdated] = useState<any>({
    groupId: {
      value: props.group?.id || "",
      error: "",
    },
    groupCSV: {
      value: {},
      error: "",
    },
  });

  const handleFieldChange = (fieldName: string, updatedValue: any) => {
    if (fieldName === "groupCSV") {
      setGroupToBeUpdated({
        ...groupToBeUpdated,
        [fieldName]: {
          value: updatedValue,
          error: "",
        },
      });
    }
  };

  const handleExportContact = async (id: string) => {
    try {
      const res = await exportGroup(id);
      openSuccessNotification(res.message);
    } catch (error: any) {
      setIsLoading(false);
      handleErrorMessage(error);
    }
  };

  const handleOnDropzone = (event: any) => {
    const fileDetails = event[0];
    const formData = new FormData();
    formData.append("file", fileDetails);
    handleFieldChange("groupCSV", fileDetails);
  };

  const handleDeleteDropZoneFile = () => {
    handleFieldChange("groupCSV", "");
  };

  const handleValidation = () => {
    let isValid = true;
    const errors = groupToBeUpdated;
    const groupCSV = groupToBeUpdated.groupCSV.value;

    if (!groupCSV.name) {
      errors.groupCSV.error = "Please upload the updated group csv";
      isValid = false;
    }
    setGroupToBeUpdated({ ...errors });
    return isValid;
  };

  const handleConfirmUpload = async () => {
    try {
      if (handleValidation()) {
        setIsLoading(true);
        const data = new FormData();
        const file = groupToBeUpdated.groupCSV.value;
        data.append("file", file);
        const response = await updateContactListDocument(
          data,
          groupToBeUpdated.groupId.value
        );
        openSuccessNotification(response.message);
        setIsLoading(false);
        props.handleDialogClose();
        await props.getActiveData();
      }
    } catch (error: any) {
      setIsLoading(false);
      handleErrorMessage(error);
    } finally {
      setIsLoading(false);
    }
  };

  const getDialogHeader = () => {
    return (
      <>
        <Box>
          <Typography
            variant="h4"
            fontWeight={600}
            color={theme.palette.primary.main}
            pb={2}
          >
            Update Group
          </Typography>
          <Divider />
        </Box>
      </>
    );
  };
  const getDialogBody = () => {
    return (
      <Stack direction="column" spacing={2}>
        <Box>
          <Typography variant="body2">
            You're attempting to update an existing group{" "}
            <span
              style={{
                fontWeight: 700,
              }}
            >
              {props.group.name}
            </span>
            . Please follow the steps below to proceed:
          </Typography>

          <Box component="ol" sx={{ pl: 2 }}>
            <li>
              <Typography variant="body2">
                <Box
                  component="span"
                  sx={{
                    color: "#7551D0",
                    cursor: "pointer",
                    textDecoration: "underline",
                  }}
                  onClick={() => handleExportContact(props.group?.id)}
                >
                  Click here
                </Box>{" "}
                to download the current list of contacts in this group.
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                Make the necessary updates directly in the downloaded CSV file.
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                Upload the same CSV file after making your changes.
              </Typography>
            </li>
            <Typography variant="body2">
              <Box component="span" sx={{ fontWeight: 600 }}>
                Note:
              </Box>{" "}
              New CSV files cannot be uploaded here.
            </Typography>
          </Box>

          <Typography variant="body2" sx={{ mt: 2, fontWeight: 600 }}>
            <Stack
              p={1}
              sx={{ backgroundColor: "#f4f1fc", borderRadius: "8px" }}
            >
              <Typography variant="body2">
                <Box component="span" sx={{ fontWeight: 600 }}>
                  Important:{" "}
                </Box>
                The "Update Group" function only allows changes to existing
                contacts. If you need to add new contacts, please create a new
                group instead.
              </Typography>
            </Stack>
          </Typography>
        </Box>
        <Grid container pt={1}>
          <Grid item xs={12}>
            <CustomDropzone
              sx={{ height: "8rem" }}
              onAdd={(file: any) => {
                handleOnDropzone(file);
              }}
              maxFileSize={85 * 1024 * 1024}
              acceptedFiles={[".csv"]}
              showAlerts={false}
              multipleFileUpload={false}
              dropzoneText={"Choose a file or drag & drop it here"}
              icon={DropZoneIcon}
            />
            {isTruthy(groupToBeUpdated.groupCSV.error) && (
              <FormHelperText error sx={{ padding: 1 }}>
                {groupToBeUpdated.groupCSV.error}
              </FormHelperText>
            )}
            {isTruthy(groupToBeUpdated?.groupCSV?.value?.name) && (
              <Stack direction="row" spacing={2} py={2}>
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{
                    backgroundColor: "#F4F1FC",
                    p: { xs: 1, md: 1 },
                    borderRadius: "5px",
                  }}
                >
                  <Typography variant="subtitle1" color="#7551D0">
                    {groupToBeUpdated?.groupCSV?.value?.name}
                  </Typography>
                  <IconButton sx={{ p: 0 }} onClick={handleDeleteDropZoneFile}>
                    <CloseRoundedIcon htmlColor="#7551D0" fontSize="small" />
                  </IconButton>
                </Stack>
              </Stack>
            )}
          </Grid>
        </Grid>
      </Stack>
    );
  };
  const getFooter = () => {
    return (
      <Stack direction="row" justifyContent="center" spacing={2}>
        <Button
          id="contacts_add__cancel_btn"
          variant="outlined"
          onClick={() => props.handleDialogClose()}
          sx={{
            width: "7.125rem",
            height: "2.5rem",
          }}
        >
          Cancel
        </Button>
        <Button
          id="contacts_add_save_btn"
          variant="contained"
          onClick={() => handleConfirmUpload()}
          sx={{
            width: "7.125rem",
            height: "2.5rem",
          }}
        >
          Update
        </Button>
      </Stack>
    );
  };
  return (
    <>
      <CustomLoader isLoading={isLoading} />
      <CustomDialog
        handleDialogClose={() => props.handleDialogClose()}
        isDialogOpen={props.isVisible}
        body={getDialogBody()}
        footer={getFooter()}
        title={getDialogHeader()}
        closeButtonVisibility={true}
        closable
        maxWidth="md"
      />
    </>
  );
};

export default UpdateGroupModal;
